/**
 * photo-work/permissions — ai thấy trang "Sửa ảnh" (/photo-work).
 *
 * Gương của cổng BE: `auth.AllowOrDept("photo.retouch.read", "photo_idphoto",
 * "photo_profile", "photo_corporate")` trong ekipschedule/handler.go. FE chỉ
 * ẩn/hiện menu và chặn sớm; BE mới là chốt chặn thật.
 */

import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { useAuthV2 } from "@/lib/authv2/store";
import type { PhotoWorkScope } from "./api";
import { opsRole } from "@/lib/auth/page-access";

type PermUser = Pick<Staff, "id" | "role" | "department" | "jobTitleCode"> | null | undefined;

/**
 * Đúng ba mã phòng ảnh mà cổng BE liệt kê — KHÔNG có mã nhóm chung `photo`.
 * `AllowOrDept` so `strings.ToLower(TrimSpace(u.Department))` với đúng bộ này,
 * nên thêm `photo` vào đây là FE mời người vào rồi BE trả 403 (đã đo: token
 * role `staff`/`qc` mang department `photo` bị chặn).
 *
 * Leader khối ảnh mang department `photo` vẫn vào được — nhưng bằng permission
 * `photo.retouch.read` của role manager, không phải bằng mã phòng. Khác cố ý
 * với `PHOTO_DEPTS` bốn mã trong ekip-shift/permissions.ts: cổng bên đó là
 * luật riêng của service ca, không phải cổng này.
 */
export const PHOTO_WORK_DEPTS: ReadonlySet<string> = new Set([
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
]);

export function canReadPhotoWork(user: PermUser): boolean {
  if (!user) return false;
  if (hasPermission(ACTION.PhotoRetouchRead)) return true;
  // Cùng phép chuẩn hoá với BE (`ToLower(TrimSpace(...))`) để hai bên không
  // lệch nhau vì một dấu cách.
  return PHOTO_WORK_DEPTS.has((user.department ?? "").trim().toLowerCase());
}

/**
 * Phạm vi mặc định khi mở trang: người đọc được hàng đợi toàn cục (leader,
 * điều phối, admin) nhìn cả chi nhánh; thợ nhìn đơn của mình.
 *
 * Đọc PHẢN ỨNG từ store, không chụp một lần: quyền nạp cùng session nên tới
 * SAU lần render đầu — component gọi hook này sẽ vẽ lại khi quyền về, leader
 * không bị kẹt ở "mine" (đúng bẫy đã ghi ở EkipShiftsView.tsx:61). KHÔNG thay
 * bằng `hasPermission(...)` đọc một lần rồi bỏ vào `useState`.
 */
export function useDefaultPhotoWorkScope(): PhotoWorkScope {
  return useAuthV2((s) => (s.permissions.has(ACTION.PhotoRetouchRead) ? "branch" : "mine"));
}

/** Vai chuyển được MỌI đơn ảnh thẻ trong phạm vi — gương `retouchTransferAnyoneInScope` ở máy chủ. */
const TRANSFER_ANY_ROLES: ReadonlySet<string> = new Set(["admin", "director", "branch_manager"]);
/** Đơn đã chụp xong — gương điều kiện trạng thái của POST /orders/{id}/retouch-transfer. */
const TRANSFER_STATUSES: ReadonlySet<string> = new Set(["editing", "completed", "delivered"]);

/**
 * Nút "Chuyển hậu kỳ" (QĐ 0368, chốt 1b · 9a): có quyền `photo.retouch_transfer`;
 * đơn có tên mình là thợ chụp, hoặc vai quản trị / giám đốc / trưởng cửa hàng;
 * đơn chưa chuyển, đã chụp xong, và dòng sản phẩm không vốn qua hậu kỳ. Máy
 * chủ là chốt chặn thật — ở đây chỉ để không bày nút bấm rồi ăn 403/409.
 */
export function canTransferToRetouch(
  user: PermUser,
  item: { status: string; isMine: boolean; retouchTransferred: boolean },
  lineNeedsRetouch: boolean,
): boolean {
  if (!user || !hasPermission(ACTION.PhotoRetouchTransfer)) return false;
  if (lineNeedsRetouch || item.retouchTransferred || !TRANSFER_STATUSES.has(item.status)) return false;
  // Trưởng ca chuyển như Trưởng cửa hàng — gương `newTransferActor` (QĐ 0425).
  return item.isMine || TRANSFER_ANY_ROLES.has(opsRole(user).trim().toLowerCase());
}
